import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { AttributeTypeModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import { useEffect, useState } from "react";

const enum fields {
    hideLogo = "hideLogo",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.hideLogo]: boolean;
    };
}
/* Store Custom Code - End */

type Props = OwnProps & HasProduct; // Store Custom Code

export interface ProductDetailsAttributesStyles {
    wrapper?: GridContainerProps; // Store Custom Code
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // Store Custom Code
    list?: GridItemProps;
    item?: TypographyPresentationProps;
    noResult?: TypographyPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        gap: 20, // Store Custom Code
        css: css`
            /* Store Custom Code - Start */
            width: 100%;
            display: flex;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["0", null, "20px", null, null], properties: ["margin-top"] }],
                    mediaOptions.min,
                )}/* Store Custom Code - End */
        `,
    },
    attributeTypeLabelText: {
        weight: TYPOGRAPHY.weight.bold, // Store Custom Code
        // Store Custom Code
        color: COLORS.text.base,
        css: css`
            width: calc(50% - 0.5em);
        `,
    },
    attributeTypeValuesText: {
        css: css`
            width: calc(50% - 0.5em); /* Store Custom Code */
        `,
    },
    // Store Custom Code
    list: {
        width: [12, 12, 6, 6, 6],
        css: css`
            display: flex;
            flex-flow: column nowrap;
            word-wrap: break-word;
            padding-top: 0;
            padding-bottom: 0;

            & + ul {
                li:nth-child(even) {
                    ${({ theme }) =>
                        breakpointValuesMediaQueries(
                            theme,
                            [
                                {
                                    values: [`${COLORS.background.alt}`, null, "transparent", null, null],
                                    properties: ["background-color"],
                                },
                            ],
                            mediaOptions.min,
                        )}
                }

                li:nth-child(odd) {
                    ${({ theme }) =>
                        breakpointValuesMediaQueries(
                            theme,
                            [
                                {
                                    values: ["transparent", null, `${COLORS.background.alt}`, null, null],
                                    properties: ["background-color"],
                                },
                            ],
                            mediaOptions.min,
                        )}
                }
            }
        `,
    },
    item: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            padding: 1em 1.25em;
            background-color: ${COLORS.background.base};
            width: 100%;

            &:nth-child(odd) {
                background-color: ${COLORS.background.alt};
            }
        `,
    },
    noResult: {
        forwardAs: "p",
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes = ({ product: { brand, attributeTypes }, fields }: Props /* Store Custom Code */) => {
    /* Store Custom Code - Start */
    const [sortedAttributes, setSortedAttributes] = useState<AttributeTypeModel[]>();
    const [firstHalfAttributes, setFirstHalfAttributes] = useState<AttributeTypeModel[]>();
    const [secondHalfAttributes, setSecondHalfAttributes] = useState<AttributeTypeModel[]>();

    useEffect(() => {
        if (attributeTypes && attributeTypes.length > 0) {
            const sortedData = [...(attributeTypes as AttributeTypeModel[])];

            if (sortedData !== undefined) {
                sortedData.sort((a, b) => a.sortOrder - b.sortOrder);
            }

            setSortedAttributes(sortedData);

            if (attributeTypes !== undefined && attributeTypes.length > 0) {
                const halfArray = Math.ceil(sortedData.length / 2);
                setFirstHalfAttributes(sortedData.slice(0, halfArray - (brand ? 1 : 0)));
                setSecondHalfAttributes(sortedData.slice(brand ? -halfArray : halfArray));
            }
        }
    }, [attributeTypes]);

    const { hideLogo } = fields;
    const ProductDetailsSectionTitle = getWidgetComponent("ProductDetails/ProductDetailsSectionTitle");

    const renderAttributes = (attributeType: AttributeTypeModel) => {
        return (
            <Typography
                as="li"
                key={attributeType.id.toString()}
                data-test-selector="attributes_item"
                data-attributetypeid={attributeType.id}
                {...styles.item}
            >
                <Typography data-test-selector="attributes_item_label" {...styles.attributeTypeLabelText}>
                    {attributeType.label || attributeType.name}&nbsp;:&nbsp;
                </Typography>
                <Typography data-test-selector="attributes_item_value" {...styles.attributeTypeValuesText}>
                    {(attributeType.attributeValues ?? [])
                        .map(attributeValue => attributeValue.valueDisplay)
                        .join(", ")}
                </Typography>
            </Typography>
        );
    };

    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    return (
        <>
            {(brand || sortedAttributes) && (
                <ProductDetailsSectionTitle sectionTitleValue={translate("Attributes")} hideLogo={hideLogo} />
            )}
            <GridContainer {...styles.wrapper}>
                <GridItem as="ul" {...styles.list} data-test-selector="productDetails_attributes">
                    {brand && (
                        <Typography as="li" data-test-selector="brand_item" {...styles.item}>
                            <Typography data-test-selector="brand_item_label" {...styles.attributeTypeLabelText}>
                                {translate("Brand")}
                            </Typography>
                            <Typography data-test-selector="brand_item_value" {...styles.attributeTypeValuesText}>
                                {brand.name}
                            </Typography>
                        </Typography>
                    )}
                    {firstHalfAttributes && firstHalfAttributes.map(attributeType => renderAttributes(attributeType))}
                </GridItem>
                <GridItem as="ul" {...styles.list} data-test-selector="productDetails_attributes">
                    {secondHalfAttributes && secondHalfAttributes.map(attributeType => renderAttributes(attributeType))}
                </GridItem>
            </GridContainer>
        </>
    );
    /* Store Custom Code - End */
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        // Store Custom Code
        fieldDefinitions: [
            {
                name: fields.hideLogo,
                displayName: "Hide Logo in Section Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
